import React, { useMemo, useState } from 'react';
import { InputNumber, Space, Typography } from 'antd';
import { QRCodeSVG } from 'qrcode.react';
import { PrintPreviewChrome } from '@/components/print/PrintPreviewChrome';
import { buildTmmdeLabel, type TmmdeLabelVariant } from '@/print-engine/adapters/tmmde-label-adapter';
import type { TmmdeEquipmentRow } from '@/services/calibration.service';

const TONE_COLORS = {
  ok: { band: '#237804', text: '#fff' },
  warn: { band: '#d46b08', text: '#fff' },
  stop: { band: '#cf1322', text: '#fff' },
} as const;

/** Physical label size (mm) — fits a typical instrument tag. */
const LABEL_W = 70;
const LABEL_H = 42;

export function TmmdeEquipmentLabelPrint(props: {
  equipment: TmmdeEquipmentRow;
  variant: TmmdeLabelVariant;
  reason?: string | null;
  onClose: () => void;
}) {
  const [copies, setCopies] = useState(1);
  const model = useMemo(
    () =>
      buildTmmdeLabel(props.equipment, props.variant, {
        origin: typeof window !== 'undefined' ? window.location.origin : undefined,
        reason: props.reason,
      }),
    [props.equipment, props.variant, props.reason]
  );
  const tone = TONE_COLORS[model.tone];
  const fileName = `${props.equipment.asset_code || 'equipment'}-${props.variant === 'NOT_FOR_USE' ? 'not-for-use' : 'calibration'}-label`;

  const label = (key: number) => (
    <div
      key={key}
      style={{
        width: `${LABEL_W}mm`,
        height: `${LABEL_H}mm`,
        border: `0.6mm solid ${tone.band}`,
        borderRadius: '1.5mm',
        boxSizing: 'border-box',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        fontFamily: 'Arial, Helvetica, sans-serif',
        color: '#000',
        background: '#fff',
        pageBreakInside: 'avoid',
        breakInside: 'avoid',
      }}
    >
      <div
        style={{
          background: tone.band,
          color: tone.text,
          fontWeight: 700,
          fontSize: model.variant === 'NOT_FOR_USE' ? '5mm' : '4mm',
          letterSpacing: '0.4mm',
          textAlign: 'center',
          padding: '0.8mm 0',
          WebkitPrintColorAdjust: 'exact',
          printColorAdjust: 'exact',
        }}
      >
        {model.heading}
      </div>
      <div style={{ display: 'flex', flex: 1, padding: '1.2mm 1.5mm', gap: '1.5mm', minHeight: 0 }}>
        <table style={{ flex: 1, borderCollapse: 'collapse', fontSize: '2.6mm', lineHeight: 1.25 }}>
          <tbody>
            {model.rows.map((r) => (
              <tr key={r.label}>
                <td style={{ fontWeight: 700, whiteSpace: 'nowrap', paddingRight: '1mm', verticalAlign: 'top' }}>
                  {r.label}:
                </td>
                <td style={{ wordBreak: 'break-word' }}>{r.value}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div style={{ width: '18mm', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <QRCodeSVG value={model.qrPayload} size={64} style={{ width: '18mm', height: '18mm' }} />
        </div>
      </div>
      {model.note && (
        <div
          style={{
            borderTop: `0.3mm solid ${tone.band}`,
            fontSize: '2.4mm',
            fontWeight: 700,
            padding: '0.6mm 1.5mm',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {model.note}
        </div>
      )}
    </div>
  );

  return (
    <PrintPreviewChrome
      title={props.variant === 'NOT_FOR_USE' ? 'NOT FOR USE label' : 'Calibration status label'}
      fileName={fileName}
      onBack={props.onClose}
      extraToolbar={
        <Space size={4}>
          <Typography.Text type="secondary">Copies</Typography.Text>
          <InputNumber min={1} max={24} value={copies} onChange={(v) => setCopies(Number(v) || 1)} size="small" />
        </Space>
      }
    >
      <div
        className="print-doc-a4"
        style={{
          padding: '10mm',
          display: 'flex',
          flexWrap: 'wrap',
          gap: '4mm',
          alignContent: 'flex-start',
          background: '#fff',
        }}
      >
        {Array.from({ length: copies }, (_, i) => label(i))}
      </div>
    </PrintPreviewChrome>
  );
}

export default TmmdeEquipmentLabelPrint;
